import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'

import { useGlobalConfigContext } from '../../../global-config/context/globalConfigContext'
import { useProductStockStatus } from '../../../product-page/components/ProductBuyBox/hooks/useProductStockStatus'
import { useProductPageContext } from '../../../product-page/components/productPageContext'
import { PRODUCT_TEMPLATES } from '../../../product-page/model/productTemplates'
import { useGetExperiment } from '../../hooks'
import { EXPERIMENT_NAME_PDP_DELIVERY_DATES_FOR_OTC } from '../../model/EXPERIMENT_NAME_PER_TENANT'
import { EXPERIMENT_VARIATION } from '../../model/EXPERIMENT_VARIATION'
import { type ExperimentType } from '../../model/Experiment.types'
import { experimentAccessorGetExperimentNamePerTenant } from '../../model/experimentAccessor'
import { useExperimentsContext } from '../ExperimentsContext'

export const useDeliveryInfoExperiment = (): {
  deliveryDatesExperiment: ExperimentType
  getDeliveryLabelMessage: () => JSX.Element
  isOTProduct: boolean
  useActivateDeliveryDatesExperiment: () => void
} => {
  const { product, productTemplate } = useProductPageContext()
  const { tenant } = useGlobalConfigContext()

  // Use the existing accessor function instead of manual type casting
  const experimentName = experimentAccessorGetExperimentNamePerTenant({
    experimentName: EXPERIMENT_NAME_PDP_DELIVERY_DATES_FOR_OTC,
    tenant,
  })

  const deliveryDatesExperiment = useGetExperiment(experimentName ?? '')

  // Constants for excluded levels
  const LEVEL_MINUS_ONE = -1

  const EXCLUDED_LEVELS = [LEVEL_MINUS_ONE] as const

  // Filter out inventory items with level -1 (later maybe needed to add more levels)
  const inventoryWithoutExcludedLevels = product.offers?.[0]?.inventory?.filter(
    (item) =>
      item.level !== undefined &&
      !EXCLUDED_LEVELS.includes(item.level as (typeof EXCLUDED_LEVELS)[number]),
  )

  // Experiment is true when product is OTC/OTX AND has inventory items after filtering out level -1
  const isOTProduct =
    (productTemplate === PRODUCT_TEMPLATES.OTC ||
      productTemplate === PRODUCT_TEMPLATES.OTX) &&
    Boolean(inventoryWithoutExcludedLevels?.length)

  const useActivateDeliveryDatesExperiment = (): void => {
    const { addExperimentKeysForActivation } = useExperimentsContext()

    useEffect(() => {
      if (isOTProduct && deliveryDatesExperiment.isEnabled && experimentName) {
        addExperimentKeysForActivation(experimentName)
      }
    }, [addExperimentKeysForActivation])
  }

  // Helper function to create message maps for all countries
  const createMessageMap = (): Partial<
    Record<EXPERIMENT_VARIATION, JSX.Element>
  > => {
    return {
      [EXPERIMENT_VARIATION.DEFAULT]: (
        <FormattedMessage id="general.label.product.available" />
      ),
      [EXPERIMENT_VARIATION.V1]: (
        <FormattedMessage id="general.label.product.availableDays" />
      ),
      [EXPERIMENT_VARIATION.V2]: (
        <FormattedMessage id="general.label.product.availableDaysV2" />
      ),
    }
  }

  const messageMap = createMessageMap()
  const productStockStatus = useProductStockStatus()

  const getDeliveryLabelMessage = (): JSX.Element => {
    if (
      !isOTProduct ||
      !deliveryDatesExperiment.isEnabled ||
      !deliveryDatesExperiment.variant ||
      deliveryDatesExperiment.variant === EXPERIMENT_VARIATION.DEFAULT
    ) {
      return (
        productStockStatus.message ?? (
          <FormattedMessage id="general.label.product.available" />
        )
      )
    }

    return (
      messageMap[deliveryDatesExperiment.variant] ?? (
        <FormattedMessage id="general.label.product.available" />
      )
    )
  }

  return {
    deliveryDatesExperiment,
    getDeliveryLabelMessage,
    isOTProduct,
    useActivateDeliveryDatesExperiment,
  }
}
